/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect } from 'react';
import { Checkbox, message, Modal, Spin } from 'antd';
import { CourseType, TermSettingType, EventType, TermType } from 'types/course.type';
import fa from 'locales/fa.json';
import AntButton from 'components/Common/AntButton';
import { faNumber } from 'utils/common.util';
import request from 'services/request';
import { DirectPayUrl, discountUrl, UpdateUserInfoUrl } from 'services/routes';
import { StyledButton } from 'components/Common/commonStyle';
import { getChapterAction } from 'store/course/course.action';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { getUserAction } from 'store/account/account.action';
import { useForm } from 'react-hook-form';
import FormInput from 'components/Common/FormInput';
import FormRadio from 'components/Common/FormRadio';
import FormSelect from 'components/Common/FormSelect';
import cities from 'public/cities.json';
import Cookies from 'js-cookie';

type ModalType = {
  isVisible: boolean;
  setIsVisible: (status) => void;
  setTryRegister: (status) => void;
  data:
    | CourseType
    | EventType
    | TermType
    | { price: number; registered: boolean; id: string; title: string; discount: number }
    | any; // eslint-disable-line
  termData?: TermSettingType;
  termType: number;
};
const TermRegisterModal: React.FC<ModalType> = (props) => {
  const { isVisible, setIsVisible, data, termData, setTryRegister, termType } = props;
  const { handleSubmit, control, setValue, watch } = useForm();
  const id = data?.id;
  const router = useRouter();
  const dispatch = useDispatch();
  const { eventId, termId, examId, courseId } = router.query;
  const [code, setCode] = useState('');
  const [checkInfo, setCheckInfo] = useState(false);
  const type = eventId ? 'event' : examId ? 'exam' : termId ? 'term' : 'workshop';
  const [loading, setLoading] = useState(null);
  const [discountPrice, setDiscountPrice] = useState(null);
  const price = discountPrice || Number(data?.price * ((100 - data?.discount) / 100));
  const user = useSelector((state) => state.account.user);
  const discountAmount = Number(data?.price) - discountPrice;

  useEffect(() => {
    setValue('city_title', null);
  }, [watch('province_title')]);

  const handleDiscount = async (): Promise<void> => {
    const body = { code, for: type, id_for: id, term_type: termType }; // eslint-disable-line
    setLoading('discount');
    const res: any = await request.post(discountUrl(), body); // eslint-disable-line
    setLoading(null);
    res.data.code ? message.success(res.data.msg) : message.warn(res.data.msg);
    res.data.code && setDiscountPrice(Number(res.data.price));
  };

  const handlePay = async (method): Promise<void> => {
    const refId = Cookies.get('taalei-ref');
    const href = DirectPayUrl(id, type);

    let body: any = { code, useWallet: 0, refId }; // eslint-disable-line
    body = termData ? { ...body, term_type: termData.term_type } : body; // eslint-disable-line
    setLoading(method);
    const res: any = await request.post(href, body); // eslint-disable-line
    setLoading(null);
    if (!res.ok) return;
    if (method === 'direct') {
      window.location.assign(res.data.link);
    } else if (res.ok) {
      message.success(res.data.message);
      dispatch(getUserAction()); // we need to get new user with this new course on it's workshop
      examId && router.replace(router.asPath);
      courseId && dispatch(getChapterAction(courseId)); // if user register on lesson or course page
      setIsVisible('');
      setTryRegister(false);
    }
  };

  const onApprove = async (formData): Promise<void> => {
    const province = cities.find((city) => city.title === formData.province_title);
    const province_id = province.id;
    const city_id = province.cities.find((city) => city.title === formData.city_title).id;
    setLoading('info');
    const res = await request.post(UpdateUserInfoUrl(), {
      ...formData,
      province_id,
      city_id,
    });
    setLoading(null);
    res.ok && setCheckInfo(true);
  };

  useEffect(() => {
    if (user) {
      setValue('name', user.name);
      setValue('family', user.family);
      setValue('nickname', user.nickname);
      setValue('sex', user.sex);
      setValue('birthYear', user.info.birthYear);
      setValue('province_title', user.info.province_title);
      setValue('city_title', user.info.city_title);
    }
  }, [user]);

  return (
    <Modal
      title={null}
      footer={null}
      width={600}
      destroyOnClose
      visible={isVisible}
      onCancel={(): void => (
        setIsVisible(''), setTryRegister(false), setDiscountPrice(null)
      )}
    >
      <div className="flex-col ">
        <div className="font-bold text-[24px] border-b border-b-gray-1 w-full text-center pb-[15px]">
          {data?.title} {`(${termData?.name})`}
        </div>

        <div className="my-[20px] flex w-full">
          <input
            className="border flex-1 min-w-0 border-gray-1 text-[18px] toRight p-[5px] ml-[12px] focus:border-gray-2 rounded-[6px]"
            onChange={(e): void => setCode(e.target.value)}
            placeholder={fa.global.discount}
            value={code}
            type="text"
          />
          <AntButton
            width="120px"
            height="40px"
            fontSize={18}
            loading={loading === 'discount'}
            onClick={handleDiscount}
            disabled={!code.length}
          >
            {fa.global.apply}
          </AntButton>
        </div>

        <div className="flex w-full text-[18px]">
          {fa.course.payAmount} : {faNumber(price.toLocaleString())}{' '}
          {fa.global.simpleTooman}
        </div>
        {discountPrice && (
          <div className="flex w-full text-[16px] mt-[8px] text-green-3">
            {fa.course.discountAmount} {faNumber(discountAmount.toLocaleString())}
            {fa.global.simpleTooman}
          </div>
        )}

        <div className="flex w-full text-[16px] mt-[24px]">
          {fa.global.walletCredit} :{' '}
          {!user?.price
            ? fa.global.noCredit
            : faNumber(Number(user?.price).toLocaleString())}
        </div>

        <form onSubmit={handleSubmit(onApprove)}>
          <FormInput
            control={control}
            name="name"
            rtl
            className="mt-12"
            rules={{ required: true }}
            title={fa.global.name}
          />
          <FormInput
            control={control}
            name="family"
            rtl
            className="mt-12"
            rules={{ required: true }}
            title={fa.global.family}
          />
          <FormInput
            control={control}
            name="nickname"
            rtl
            className="mt-12"
            rules={{ required: true }}
            title={fa.account.nickname}
          />
          <FormInput
            control={control}
            name="birthYear"
            className="mt-12"
            type="number"
            rules={{ required: true, maxLength: 4, minLength: 4 }}
            title={fa.global.birthYear}
          />
          <div className="flex mt-12">
            <div className="ml-4">{fa.global.sex}: </div>
            <FormRadio
              name="sex"
              control={control}
              rules={{ required: true }}
              options={[
                { value: 1, title: fa.global.man },
                { value: 2, title: fa.global.woman },
              ]}
            />
          </div>

          <div className="flex mt-10 relative">
            <div className="absolute flex">
              <FormSelect
                control={control}
                name="province_title"
                className="w-[150px] ml-6"
                placeholder={fa.global.province}
                rules={{ required: true }}
                options={cities?.map((city) => ({
                  value: city.title,
                  label: city.title,
                }))}
              />
              <FormSelect
                control={control}
                name="city_title"
                disabled={!watch('province_title')}
                className="w-[150px]"
                placeholder={fa.global.city}
                rules={{ required: true }}
                options={cities
                  ?.find((c) => c.title === watch('province_title'))
                  ?.cities.map((city) => ({ value: city.title, label: city.title }))}
              />
            </div>
          </div>

          <div className="mt-20 text-[20px]">
            <Checkbox type="submit" checked={checkInfo}>
              {fa.term.approveInfo} {loading === 'info' && <Spin />}
            </Checkbox>
          </div>
        </form>

        <div className="flex flex-col w-full mt-8 text-[16px]">
          <div className="mb-[8px]">{fa.course.choosePayMethod}</div>
          <div className="flex flex-col md:flex-row ">
            <StyledButton
              type="primary"
              fontSize={16}
              ml="12px"
              mb={3}
              height="40px"
              loading={loading === 'direct'}
              borderRadius="8px"
              disabled={!checkInfo}
              onClick={(): Promise<void> => handlePay('direct')}
            >
              {fa.course.directRegister}
            </StyledButton>

            {/* <Popconfirm
              title={fa.global.areYouSure}
              onConfirm={(): Promise<void> => handlePay('wallet')}
              okText={fa.global.yes}
              showCancel={false}
              key="wallet"
            > */}
            <StyledButton
              type="primary"
              disabled={!checkInfo}
              onClick={(): Promise<void> => handlePay('wallet')}
              fontSize={16}
              loading={loading === 'wallet'}
              ml="12px"
              height="40px"
              borderRadius="8px"
            >
              {fa.course.walletRegister}
            </StyledButton>
            {/* </Popconfirm> */}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TermRegisterModal;
